import React, { useEffect, useState, useCallback } from 'react';
import { Box, Group, TextInput, Select, Badge, ActionIcon, Button, Text } from '@mantine/core';
import { IconRefresh, IconPlus } from '@tabler/icons-react';
import { Link, useSearchParams } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import useApi from '../hooks/useApi';
import { fetchCampaigns, fetchLeads, updateLead } from '../services/api';
import PipelineBoard from '../components/PipelineBoard';
import LoadingSpinner from '../components/LoadingSpinner';

const TIME_FILTER_OPTIONS = [
  { value: '',    label: 'Full Campaign' },
  { value: '24h', label: 'Last 24 hours' },
  { value: '7d',  label: 'Last 7 days' },
  { value: '30d', label: 'Last month' },
];

function getTimeThreshold(filter) {
  if (filter === '24h') return new Date(Date.now() - 24 * 60 * 60 * 1000);
  if (filter === '7d')  return new Date(Date.now() - 7  * 24 * 60 * 60 * 1000);
  if (filter === '30d') return new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
  return null;
}

export default function PipelinePage() {
  const api = useApi();
  const [searchParams] = useSearchParams();
  const [campaigns, setCampaigns] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filter state — seed campaignId from URL so back-nav from lead detail restores filter
  const [filterCampaign, setFilterCampaign] = useState(() => searchParams.get('campaignId') || '');
  const [filterTime, setFilterTime] = useState('');
  const [search, setSearch] = useState('');

  const loadCampaigns = useCallback(async () => {
    try {
      const data = await fetchCampaigns(api);
      setCampaigns(data.campaigns || []);
    } catch (err) {
      notifications.show({
        title: 'Failed to load campaigns',
        message: err.response?.data?.error || 'Something went wrong',
        color: 'red',
      });
    }
  }, [api]);

  const loadLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterCampaign) params.campaignId = filterCampaign;
      const data = await fetchLeads(api, params);
      setLeads(data.leads || []);
    } catch (err) {
      notifications.show({
        title: 'Failed to load leads',
        message: err.response?.data?.error || 'Something went wrong',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  }, [api, filterCampaign]);

  useEffect(() => { loadLeads(); }, [loadLeads]);
  useEffect(() => { loadCampaigns(); }, [loadCampaigns]);

  async function handleStageDrop(leadId, newStage) {
    setLeads(prev => prev.map(l => l.id === leadId ? { ...l, stage: newStage } : l));
    try {
      await updateLead(api, leadId, { stage: newStage });
    } catch (err) {
      notifications.show({
        title: 'Failed to move lead',
        message: err.response?.data?.error || 'Something went wrong',
        color: 'red',
      });
      loadLeads();
    }
  }

  const timeThreshold = getTimeThreshold(filterTime);

  const activeCampaigns = campaigns.filter(c => c.status === 'active' || c.status === 'draft');
  const hiddenCampaignIds = new Set(
    campaigns.filter(c => c.status === 'archived' || c.status === 'completed' || c.status === 'paused').map(c => c.id)
  );

  const displayed = leads.filter(l => {
    if (l.archived) return false;
    if (l.campaignId && hiddenCampaignIds.has(l.campaignId)) return false;
    if (search) {
      const q = search.toLowerCase();
      if (
        !l.name?.toLowerCase().includes(q) &&
        !l.company?.toLowerCase().includes(q) &&
        !l.email?.toLowerCase().includes(q)
      ) return false;
    }
    if (timeThreshold && new Date(l.createdAt) < timeThreshold) return false;
    return true;
  });

  const aggregateStageCounts = activeCampaigns.reduce((acc, campaign) => {
    for (const [stage, count] of Object.entries(campaign.stageCounts || {})) {
      acc[stage] = (acc[stage] || 0) + Number(count || 0);
    }
    return acc;
  }, {});

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box
        bg="white"
        style={{ borderBottom: '1px solid var(--mantine-color-gray-2)', flexShrink: 0 }}
      >
        <Group px="md" py="sm" wrap="wrap" gap="sm">
          <Text fw={700} size="lg">Pipeline</Text>
          <TextInput
            placeholder="Search leads…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="sm"
            w={200}
          />
          <Select
            data={[{ value: '', label: 'All campaigns' }, ...activeCampaigns.map(c => ({ value: c.id, label: c.name }))]}
            value={filterCampaign}
            onChange={(v) => setFilterCampaign(v || '')}
            placeholder="All campaigns"
            size="sm"
            w={180}
            clearable
          />
          <Select
            data={TIME_FILTER_OPTIONS}
            value={filterTime}
            onChange={(v) => setFilterTime(v || '')}
            size="sm"
            w={160}
          />
          <Group gap="xs" ml="auto" wrap="nowrap">
            <Badge variant="light" color="aviary" size="md">
              {displayed.length} leads
            </Badge>
            <ActionIcon
              variant="subtle"
              color="gray"
              onClick={loadLeads}
              loading={loading}
              title="Refresh"
            >
              <IconRefresh size={16} />
            </ActionIcon>
            <Button
              component={Link}
              to={filterCampaign ? `/leads/new?campaignId=${filterCampaign}` : '/leads/new'}
              size="sm"
              leftSection={<IconPlus size={14} />}
            >
              Add Lead
            </Button>
          </Group>
        </Group>
      </Box>

      <div style={{ flex: 1, overflow: 'auto' }}>
        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 256 }}>
            <LoadingSpinner size="lg" />
          </div>
        ) : (
          <PipelineBoard
            leads={displayed}
            onStageDrop={handleStageDrop}
            campaigns={activeCampaigns}
            stageCounts={aggregateStageCounts}
          />
        )}
      </div>
    </div>
  );
}
